import React, { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { Lock, Save, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

export const SecuritySettings: React.FC = () => {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [message, setMessage] = useState('');

    const handleUpdatePassword = async () => {
        if (newPassword !== confirmPassword) {
            setStatus('error');
            setMessage("Passwords do not match.");
            return;
        }

        if (newPassword.length < 6) {
            setStatus('error');
            setMessage("Password must be at least 6 characters.");
            return;
        }

        setLoading(true);
        setStatus('idle');

        try {
            const { error } = await supabase.auth.updateUser({
                password: newPassword
            });

            if (error) throw error;

            setStatus('success');
            setMessage("Password updated successfully!");
            setNewPassword('');
            setConfirmPassword('');
        } catch (error: any) {
            setStatus('error');
            setMessage(error.message || "Failed to update password.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm relative">
            <div className="flex items-center gap-2 mb-6 pb-4 border-b border-slate-100">
                <Lock className="text-purple-600" size={24}/>
                <h3 className="text-lg font-bold text-slate-800">Security & Login</h3>
            </div>

            <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">New Password</label>
                    <input 
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter new password"
                        className="w-full p-2.5 border rounded-lg bg-slate-50 focus:ring-2 focus:ring-purple-500 outline-none transition-all"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Confirm Password</label>
                    <input 
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm new password"
                        className="w-full p-2.5 border rounded-lg bg-slate-50 focus:ring-2 focus:ring-purple-500 outline-none transition-all"
                    />
                </div>

                {/* Status Messages */}
                {status === 'success' && (
                    <div className="flex items-center gap-2 text-green-600 text-sm bg-green-50 p-3 rounded-lg border border-green-100 animate-in fade-in">
                        <CheckCircle2 size={16} />
                        {message}
                    </div>
                )}
                
                {status === 'error' && (
                    <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg border border-red-100 animate-in fade-in">
                        <AlertCircle size={16} />
                        {message}
                    </div>
                )}

                <button 
                    onClick={handleUpdatePassword}
                    disabled={loading || !newPassword}
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-slate-900 text-white rounded-xl hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-all"
                >
                    {loading ? <Loader2 className="animate-spin" size={18}/> : <Save size={18}/>}
                    Update Password
                </button>
            </div>
        </div>
    );
};
